import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PRICING_PLANS } from "../data/pricingPlans";
import MarketingSiteFooter from "./MarketingSiteFooter";
import MarketingSiteHeader from "./MarketingSiteHeader";

/**
 * Public pricing page (same tiers and limits as in-app billing).
 *
 * @returns {React.ReactElement} Pricing page markup.
 */
const PricingPage = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh" }}>
      <MarketingSiteHeader />

      <Box component="main">
        <Box
          id="pricing"
          component="section"
          sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.default" }}
        >
          <Container maxWidth="xl">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              Pricing
            </Typography>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 1, mb: 2 }}>
              The same pricing you already use inside Quill AI
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 6, maxWidth: 720 }}>
              Start free, then unlock Pro, Pro Plus, or Enterprise when you need more templates, sessions, and documents.
            </Typography>
            <Grid container spacing={4}>
              {PRICING_PLANS.map((plan) => (
                <Grid item xs={12} md={3} key={plan.name}>
                  <Card
                    elevation={plan.highlight ? 6 : 0}
                    sx={{
                      borderRadius: 4,
                      border: plan.highlight ? "2px solid" : "1px solid",
                      borderColor: plan.highlight ? "primary.main" : "divider",
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    {plan.highlight && (
                      <Chip
                        label="Most Popular"
                        color="secondary"
                        size="small"
                        sx={{ position: "absolute", top: 24, right: 24, fontWeight: 700 }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="h3" fontWeight={800}>
                        {plan.price}
                        <Typography component="span" variant="subtitle1" color="text.secondary">
                          {plan.cadence}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                        {plan.description}
                      </Typography>
                      <Stack spacing={1.5}>
                        {plan.features.map((feature) => (
                          <Stack direction="row" spacing={1} alignItems="center" key={feature}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                            <Typography variant="body2">{feature}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ px: 3, pb: 3 }}>
                      <Button fullWidth variant={plan.highlight ? "contained" : "outlined"} onClick={goLogin} sx={{ textTransform: "none" }}>
                        {plan.cta}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: "background.paper" }}>
          <Container maxWidth="lg">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                background: "linear-gradient(135deg, #4A00E0, #F9484A)",
                color: "white",
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                Ready to choose a plan in the app?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
                Log in or sign up to manage billing and upgrade when you are ready.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                size="large"
                variant="contained"
                color="inherit"
                endIcon={<ArrowForwardIcon />}
                sx={{ color: "primary.main", textTransform: "none", fontWeight: 700 }}
              >
                Log in to Quill AI
              </Button>
            </Paper>
          </Container>
        </Box>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default PricingPage;
